// Customer-portal API client. The portal is authenticated by a customer-scoped guest token
// (issued by the .NET magic-link flow and stored in localStorage); every call passes it as
// `?token=`. Replaces the portal's Firebase Auth + Firestore reads (#1310/#1311).

const API_BASE = (import.meta.env.VITE_MESTERPLAN_API_URL ?? '').replace(/\/$/, '');

const TOKEN_KEY = 'portalToken';

export function getPortalToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setPortalToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearPortalToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export interface PortalCustomer {
  id: string;
  name: string;
  email: string | null;
  companyId: string;
}

export interface PortalProject {
  id: string;
  title: string;
  address: string | null;
  status: string;
}

export interface PortalMessage {
  id: string;
  content: string;
  senderType: string;
  senderName: string | null;
  createdAt: string;
}

export interface PortalTimeplanPhase {
  id: string;
  projectId: string;
  companyId: string;
  kind: string;
  title: string;
  trade: string | null;
  estimatedDays: number;
  startDate: string | null;
  endDate: string | null;
  sortOrder: number;
  status: string;
  notes: string | null;
  blockers: string[];
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface PortalTimeplan {
  projectTitle: string;
  timeplanNotes: string;
  phases: PortalTimeplanPhase[];
}

/** Thrown when a portal API call returns a non-OK status. `status` lets callers detect 401/404. */
export class PortalApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'PortalApiError';
    this.status = status;
  }
}

function withToken(path: string, token: string): string {
  const sep = path.includes('?') ? '&' : '?';
  return `${API_BASE}${path}${sep}token=${encodeURIComponent(token)}`;
}

async function portalGet<T>(path: string, token: string): Promise<T> {
  const resp = await fetch(withToken(path, token), { method: 'GET' });
  if (!resp.ok) {
    throw new PortalApiError(resp.status, `Portal GET ${path} failed (${resp.status})`);
  }
  return (await resp.json()) as T;
}

// ── Auth ─────────────────────────────────────────────────────────────────────

export async function requestPortalMagicLink(email: string): Promise<void> {
  // Always resolves 200 server-side (no existence reveal); surface transport errors only.
  const resp = await fetch(`${API_BASE}/api/portal/request-magic-link`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!resp.ok) {
    throw new PortalApiError(resp.status, `request-magic-link failed (${resp.status})`);
  }
}

export function fetchPortalCustomer(token: string): Promise<PortalCustomer> {
  return portalGet<PortalCustomer>('/api/portal/me', token);
}

// ── Reads ────────────────────────────────────────────────────────────────────

export function fetchPortalProjects(token: string): Promise<PortalProject[]> {
  return portalGet<PortalProject[]>('/api/portal/projects', token);
}

export function fetchPortalMessages(projectId: string, token: string): Promise<PortalMessage[]> {
  return portalGet<PortalMessage[]>(`/api/portal/projects/${encodeURIComponent(projectId)}/messages`, token);
}

export function fetchPortalTimeplan(projectId: string, token: string): Promise<PortalTimeplan> {
  return portalGet<PortalTimeplan>(`/api/portal/projects/${encodeURIComponent(projectId)}/timeplan`, token);
}

export async function sendPortalMessage(
  projectId: string,
  content: string,
  token: string,
): Promise<PortalMessage> {
  const resp = await fetch(
    withToken(`/api/portal/projects/${encodeURIComponent(projectId)}/messages`, token),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content }),
    },
  );
  if (!resp.ok) {
    throw new PortalApiError(resp.status, `send message failed (${resp.status})`);
  }
  return (await resp.json()) as PortalMessage;
}
